import { useEffect } from 'react';

/**
 * Schedules `onUnlock` shortly after the document becomes visible while `isLoading`
 * is true. Intended to recover the social sign-in button when the user returns from
 * a cancelled native auth flow (iOS swipe-down on ASWebAuthenticationSession, Android
 * back from Chrome Custom Tabs) without an explicit cancel callback from the SDK.
 *
 * Implementation notes:
 * - The grace period (`delayMs`) gives the success path — which navigates away — time
 *   to fire before we treat the return as a cancellation.
 * - If the document goes hidden again before the timer expires (user re-launches the
 *   sheet) the pending timer is cleared, so we never unlock during a real auth.
 * - The effect is a no-op when `isLoading` is false, so visibility changes outside
 *   the social flow don't accidentally reset UI state.
 */
export const useUnlockOnReturn = (
  isLoading: boolean,
  onUnlock: () => void,
  delayMs = 1000
) => {
  useEffect(() => {
    if (!isLoading) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        timeoutId = setTimeout(onUnlock, delayMs);
      } else if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, onUnlock, delayMs]);
};
